import { ICaremapNoteResponse } from '../model/NotesClient';

export type NoteField = 'title' | 'html' | 'isPrivate' | 'tags';
const CONFLICTABLE_FIELDS: NoteField[] = ['title', 'html', 'isPrivate', 'tags'];

export interface INoteFieldConflict { field: NoteField; base: unknown; mine: unknown; theirs: unknown; }
export interface INoteMergeResult { merged: ICaremapNoteResponse; conflicts: INoteFieldConflict[]; }

export class NoteConflictDetector {
    getDirtyFields = (base: ICaremapNoteResponse, mine: ICaremapNoteResponse): NoteField[] =>
        CONFLICTABLE_FIELDS.filter(field => !this.deepEqual(mine[field], base[field]));

    getRemoteChangedFields = (base: ICaremapNoteResponse, theirs: ICaremapNoteResponse): NoteField[] =>
        CONFLICTABLE_FIELDS.filter(field => !this.deepEqual(theirs[field], base[field]));

    detect = ({ base, mine, theirs }: { base: ICaremapNoteResponse; mine: ICaremapNoteResponse; theirs: ICaremapNoteResponse; }): INoteMergeResult => {
        const dirty = this.getDirtyFields(base, mine);
        const remoteChanged = this.getRemoteChangedFields(base, theirs);
        const conflicts: INoteFieldConflict[] = [];
        // start from theirs so we inherit server-managed fields (version, updatedAt, links,
        // attachments, reactions), then re-apply MY uncommitted field edits
        const merged: ICaremapNoteResponse = this.clone(theirs);
        for (const field of CONFLICTABLE_FIELDS) {
            const isMine = dirty.includes(field);
            const isTheirs = remoteChanged.includes(field);
            // A real conflict requires the two sides to DISAGREE. When we both changed the field
            // to the same value there is nothing to resolve — common cases are the server echoing
            // my own save back to my still-open editor (a self-echo, where base is briefly stale)
            // and a co-owner making the identical edit. Without this equality guard we'd surface a
            // phantom "changed by someone else" conflict whose mine/theirs are identical, blocking
            // save. merged already holds theirs (=== mine here), so fall through to the no-op path.
            if (isMine && isTheirs && !this.deepEqual(mine[field], theirs[field])) {
                conflicts.push({ field, base: base[field], mine: mine[field], theirs: theirs[field] });
                this.assignField(merged, field, mine[field]); // hold mine until resolved
            } else if (isMine) {
                this.assignField(merged, field, mine[field]);
            }
            // else: only they changed it, neither did, or we both made the same change — merged already has theirs
        }
        return { merged, conflicts };
    };

    private assignField = (note: ICaremapNoteResponse, field: NoteField, value: unknown) => {
        // index assignment across a union of value types; value comes from the same field on a
        // sibling note, so the runtime type always matches (mirrors CareMapMerger's pattern)
        (note[field] as unknown) = value;
    };
    private clone = (note: ICaremapNoteResponse): ICaremapNoteResponse => JSON.parse(JSON.stringify(note));
    private deepEqual = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
}
